import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import SaudiRyal from "assets/saudiRyal";
import { useTranslation } from "react-i18next";

const FinancialChart = ({ data = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const processedData = React.useMemo(() => {
    return data.map((item) => ({
      name: item.month_name,
      collected: item.collected,
      returned: item.returned,
    }));
  }, [data]);
  console.log("🚀 ~ FinancialChart ~ processedData:", processedData)

  const totalCollected = React.useMemo(() => {
    return processedData.reduce((acc, item) => acc + (Number(item.collected) || 0), 0);
  }, [processedData]);

  const totalReturned = React.useMemo(() => {
    return processedData.reduce((acc, item) => acc + (Number(item.returned) || 0), 0);
  }, [processedData]);

  const totalAmount = React.useMemo(() => {
    return totalCollected + totalReturned;
  }, [totalCollected, totalReturned]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: "16px",
        boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
      }}
    >
      <Box>
        <Typography
          variant="h4"
          mb={2}
          sx={{
            fontWeight: 700,
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {totalAmount.toLocaleString()} <SaudiRyal style={{ fontSize: 16 }} />
          {/* <Typography
            variant="caption"
            sx={{
              color: theme.palette.success.main,
              bgcolor: "#e8f5e9",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontWeight: "bold",
              height: "fit-content",
            }}
          >
            ^ 9.2%
          </Typography> */}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.total_financial_amounts")}
        </Typography>
      </Box>

      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <AreaChart
            data={processedData}
            margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#09CE7D" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#09CE7D" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorReturned" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6C4192" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#6C4192" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E3E3E3"
            />
            <XAxis dataKey="name" hide />
            <YAxis hide />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="collected"
              stroke="#09CE7D"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorCollected)"
            />
            <Area
              type="monotone"
              dataKey="returned"
              stroke="#6C4192"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorReturned)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.total_collected")}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {totalCollected.toLocaleString()} <SaudiRyal width="12" height="14" />
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.total_refunded")}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {totalReturned.toLocaleString()} <SaudiRyal width="12" height="14" />
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default FinancialChart;
